import React, { useState, useMemo, useEffect } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import {
  FaChalkboardTeacher, FaUserGraduate, FaUserShield, FaUsers,
  FaChevronLeft, FaChevronRight, FaSignOutAlt, FaUserCog,
  FaUserTie, FaPlus, FaTrash, FaEnvelope, FaClipboardCheck, FaBriefcase,
  FaBuilding, FaBook, FaUserCircle, FaFileAlt, FaRobot, FaCalendarAlt, FaFingerprint,
  FaUniversity, FaGraduationCap, FaSchool
} from 'react-icons/fa';
import Cookies from 'js-cookie';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Avatar, Typography, Box, Divider
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';

// Internal Imports
import TableViewBox from './widgets/ViewBox1.jsx';
import CustomTable from './widgets/CustomTable.jsx';
import DropdownAction from './widgets/SidePannels.jsx';
import AuthDialog from './auth_screen.jsx';
import { startAuthRefresh, stopAuthRefresh } from './Session.js';
import { clearClientSession } from '@/Auth/clearSiteSession';
import { CSVOperationsManager } from './widgets/CSVOperationsHandler.jsx';
import unilinkd_icon from '../../Assets/icon2.png';
import { Prevelage } from './Scripts/rolePrivelageCheck.js';
import { refreshInstituteType, useInstituteLabels } from '@/lib/instituteLabels';
import Placements from './Placements/Placements';
import ClassroomManager from './ClassRooms/ClassRooms';
import TeachingSchedulePage from './ClassRooms/MyTimetable';
import AttendanceRegistry from './AttendanceManagement/AttendanceRegistry';
import FacultyAttendanceLog from './FacultyAttendance/FacultyAttendanceLog';
import DepartmentsPage from './Departments/DepartmentsPage';
import ClubsPage from './Clubs/ClubsPage';
import LibraryPage from './CampusLibrary/LibraryPage';
import AlumniPage from './Alumni/Alumnipage';
import DocumentsPage from './Documents/DocumentsPage';
import AIGuidePage from './AIGuide/AIGuidePage';
import SubjectRegistry from './SubjectRegistry/SubjectRegistry';

// --- Theme Constants ---
const COLORS = {
  sidebarBg: '#1a1c1e',
  sidebarHover: '#2c2f33',
  accent: '#fbc02d',
  textMain: '#ffffff',
  textSecondary: '#9da5b1',
  glassBg: 'rgba(255, 255, 255, 0.9)'
};

// --- Logic Helpers ---
// stopAuthRefresh() must come first — a refresh tick landing mid-wipe would
// write authToken straight back into a jar we just cleared.
function signOut(navigator) {
  stopAuthRefresh();
  clearClientSession();
  navigator({ to: '/login' });
}

// Pure check, no side effects — safe to call directly during render.
// signOut() (which navigates) must only ever run from an effect/handler,
// never from here, or React rejects the state update mid-render.
function _is_authorized() {
  const token = Cookies.get('authToken');
  const ins_code = Cookies.get('ins_code');
  const tokenTimestamp = Cookies.get('refreshTokenTimestamp');
  if (!token || !ins_code || !tokenTimestamp) return false;
  const ageMs = Date.now() - parseInt(tokenTimestamp, 10);
  if (ageMs > 24 * 60 * 60 * 1000) return false;
  return true;
}

function DashBoard() {
  const navigate = useNavigate();
  const [ collapsed, setCollapsed ] = useState(false);
  const [ navigationIndex, setNavigationIndex ] = useState(0);
  const [ viewKey, setViewKey ] = useState(0);
  const L = useInstituteLabels();

  // AUTH CHECK — synchronous cookie read only, no side effects
  const auth_check = _is_authorized();

  // ROLE LOGIC
  const roleService = useMemo(() => {
    if (auth_check) {
      const currentRole = Cookies.get('role');
      return new Prevelage(currentRole);
    }
    return null;
  }, [ auth_check ]);

  // Start background token refresh exactly once; clean up on unmount
  useEffect(() => {
    if (!auth_check) return;
    const intervalId = startAuthRefresh();
    return () => clearInterval(intervalId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Institute type (UI wording) — re-read once per load, so a change by
  // another admin, or a type set after this tab signed in, shows up.
  useEffect(() => {
    if (auth_check) refreshInstituteType();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // signOut() navigates — must run as an effect, not during render, or
  // React rejects the state update (this was the actual cause of the
  // dashboard hanging/erroring on any visit without a session).
  useEffect(() => {
    if (!auth_check) signOut(navigate);
  }, [ auth_check ]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!auth_check) return <AuthDialog />;

  const User_Name = Cookies.get('name');
  const User_id = Cookies.get('E_id');
  const Profile_img = Cookies.get('img') || "https://cdn.vectorstock.com/i/500p/95/56/user-profile-icon-avatar-or-person-vector-45089556.jpg";
  const User_Role = (Cookies.get('role') || 'Guest').toUpperCase();

  // Helper for selection indicator
  const isActive = (index) => navigationIndex === index;

  // Forces remount (and fresh fetch) even when clicking the active item again
  const handleNavigation = (index) => {
    if (index === navigationIndex) {
      setViewKey(k => k + 1);
    } else {
      setNavigationIndex(index);
      setViewKey(0);
    }
  };

  const renderView = () => {
    const data = [];
    const key = `${navigationIndex}-${viewKey}`;
    if ([ 1, 5, 9, 12, 15, 16 ].includes(navigationIndex)) {
      return <TableViewBox key={key} data={data} navigationIndex={navigationIndex} heading="System Dashboard" showEdit={navigationIndex !== 5} />;
    }
    if ([ 2, 3, 6, 7, 10, 23 ].includes(navigationIndex)) {
      return <CSVOperationsManager key={key} navigationIndex={navigationIndex} />;
    }
    if ([ 4, 8 ].includes(navigationIndex)) {
      return <CustomTable key={key} navigationIndex={navigationIndex} />;
    }
    if (navigationIndex === 11) return <DropdownAction key={key} />;
    if (navigationIndex === 17) return <Placements key={key} />;
    if (navigationIndex === 18) return <AttendanceRegistry key={key} />;
    if (navigationIndex === 31) return <FacultyAttendanceLog key={key} />;
    if (navigationIndex === 19) return <DepartmentsPage key={key} />;
    if (navigationIndex === 32) return <DepartmentsPage key={key} initialView="programmes" />;
    if (navigationIndex === 20) return <ClubsPage key={key} />;
    if (navigationIndex === 21) return <LibraryPage key={key} />;
    if (navigationIndex === 22) return <ClassroomManager key={key} />;
    if (navigationIndex === 30) return <TeachingSchedulePage key={key} />;
    if (navigationIndex === 24) return <AlumniPage key={key} />;
    if (navigationIndex === 26) return <DocumentsPage key={key} />;
    if (navigationIndex === 28) return <AIGuidePage key={key} />;
    if (navigationIndex === 29) return <SubjectRegistry key={key} />;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
        <img src={unilinkd_icon} alt="Logo" style={{ width: '180px', marginBottom: '20px' }} />
        <Typography variant="h5">Welcome to the UNILinkd</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* --- Sidebar --- */}
      <Sidebar
        collapsed={collapsed}
        backgroundColor={COLORS.sidebarBg}
        rootStyles={{ borderRight: 'none' }}
        width="280px"
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end' }}>
          <Button onClick={() => setCollapsed(!collapsed)} sx={{ minWidth: 'auto', color: COLORS.accent }}>
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </Button>
        </Box>

        {!collapsed && (
          <Box sx={{ px: 3, pb: 4, textAlign: 'center' }}>
            <Avatar
              src={Profile_img}
              sx={{ width: 70, height: 70, margin: '0 auto 12px', border: `2px solid ${COLORS.accent}` }}
            />
            <Typography variant="body1" fontWeight="600" sx={{ color: COLORS.textMain }}>{User_Name}</Typography>
            <Typography variant="caption" sx={{ color: COLORS.textSecondary, display: 'block', mb: 1 }}>{User_id}</Typography>
            <Box sx={{ display: 'inline-block', px: 1.5, py: 0.2, borderRadius: '4px', border: `1px solid ${COLORS.accent}` }}>
              <Typography variant="caption" sx={{ color: COLORS.accent, fontWeight: 700 }}>{User_Role}</Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />

        <Menu
          menuItemStyles={{
            button: ({ active }) => ({
              color: active ? COLORS.accent : COLORS.textSecondary,
              backgroundColor: active ? COLORS.sidebarHover : 'transparent',
              borderLeft: active ? `4px solid ${COLORS.accent}` : '4px solid transparent',
              '&:hover': {
                backgroundColor: COLORS.sidebarHover,
                color: COLORS.accent
              },
            }),
            subMenuContent: { backgroundColor: '#212327' }
          }}
        >
          <SubMenu label="Teachers" icon={<FaChalkboardTeacher />}>
            <MenuItem active={isActive(1)} onClick={() => handleNavigation(1)} icon={<FaUsers />}>Show Teachers</MenuItem>
            {roleService?.canCreateUser() && <MenuItem active={isActive(2)} onClick={() => handleNavigation(2)} icon={<FaPlus />}>Add Teachers</MenuItem>}
            {roleService?.canDeleteUser() && <MenuItem active={isActive(3)} onClick={() => handleNavigation(3)} icon={<FaTrash />}>Delete Teachers</MenuItem>}
            <MenuItem active={isActive(4)} onClick={() => handleNavigation(4)} icon={<FaEnvelope />}>Message</MenuItem>
          </SubMenu>

          <SubMenu label="Students" icon={<FaUserGraduate />}>
            <MenuItem active={isActive(5)} onClick={() => handleNavigation(5)} icon={<FaUsers />}>Show Students</MenuItem>
            {roleService?.canCreateUser() && <MenuItem active={isActive(6)} onClick={() => handleNavigation(6)} icon={<FaPlus />}>Add Students</MenuItem>}
            {roleService?.canDeleteUser() && <MenuItem active={isActive(7)} onClick={() => handleNavigation(7)} icon={<FaTrash />}>Delete Students</MenuItem>}
            <MenuItem active={isActive(8)} onClick={() => handleNavigation(8)} icon={<FaEnvelope />}>Message</MenuItem>
            {roleService?.canCreateUser() && (
              <MenuItem active={isActive(23)} onClick={() => handleNavigation(23)} icon={<FaBuilding />}>
                Reset Departments
              </MenuItem>
            )}
            <MenuItem active={isActive(24)} onClick={() => handleNavigation(24)} icon={<FaPlus />}>Manage Alumni</MenuItem>
          </SubMenu>

          <SubMenu label="Access Control" icon={<FaUserShield />}>
            <MenuItem active={isActive(12)} onClick={() => handleNavigation(12)} icon={<FaUserTie />}>Admins</MenuItem>
            <MenuItem active={isActive(15)} onClick={() => handleNavigation(15)} icon={<FaUserTie />}>Sub Admins</MenuItem>
            <MenuItem active={isActive(16)} onClick={() => handleNavigation(16)} icon={<FaUserTie />}>Super Admin</MenuItem>
          </SubMenu>

          {/* The academic structure and what runs on it, in working order:
              departments → programmes → classrooms, their subjects, attendance. */}
          <SubMenu label="Academics" icon={<FaUniversity />} defaultOpen={[ 19, 32, 22, 29, 18 ].includes(navigationIndex)}>
            <MenuItem active={isActive(19)} onClick={() => handleNavigation(19)} icon={<FaBuilding />}>{L.departments}</MenuItem>
            <MenuItem active={isActive(32)} onClick={() => handleNavigation(32)} icon={<FaGraduationCap />}>{L.programmes}</MenuItem>
            <MenuItem active={isActive(22)} onClick={() => handleNavigation(22)} icon={<FaSchool />}>{L.classrooms}</MenuItem>
            <MenuItem active={isActive(29)} onClick={() => handleNavigation(29)} icon={<FaBook />}>{L.subjects}</MenuItem>
            <MenuItem active={isActive(18)} onClick={() => handleNavigation(18)} icon={<FaClipboardCheck />}>Attendance</MenuItem>
          </SubMenu>

          <MenuItem active={isActive(30)} onClick={() => handleNavigation(30)} icon={<FaCalendarAlt />}>Teaching Schedule</MenuItem>
          <MenuItem active={isActive(31)} onClick={() => handleNavigation(31)} icon={<FaFingerprint />}>Faculty Attendance</MenuItem>
          <MenuItem active={isActive(17)} onClick={() => handleNavigation(17)} icon={<FaBriefcase />}>Placements</MenuItem>
          <MenuItem active={isActive(20)} onClick={() => handleNavigation(20)} icon={<FaUsers />}>Clubs</MenuItem>
          <MenuItem active={isActive(21)} onClick={() => handleNavigation(21)} icon={<FaBook />}>Campus Library</MenuItem>

          {roleService?.canManageDocuments() && (
            <MenuItem active={isActive(26)} onClick={() => handleNavigation(26)} icon={<FaFileAlt />}>Documents</MenuItem>
          )}

          {roleService?.canManageAIGuide() && (
            <MenuItem active={isActive(28)} onClick={() => handleNavigation(28)} icon={<FaRobot />}>AI-Guide</MenuItem>
          )}

          <Box sx={{ mt: 4 }}>
            <MenuItem icon={<FaSignOutAlt />} onClick={() => signOut(navigate)}>Sign Out</MenuItem>
          </Box>
        </Menu>
      </Sidebar>

      {/* --- Main Content Area --- */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflowY: 'auto',
          position: 'relative',
          p: 3,
          background: `linear-gradient(135deg, #ffde59, #8b3b05)`,
        }}
      >
        <img
          src={unilinkd_icon}
          alt="bg"
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', opacity: 0.05,
            width: '300px', pointerEvents: 'none'
          }}
        />

        <Box sx={{
          position: 'relative',
          zIndex: 1,
          background: COLORS.glassBg,
          borderRadius: '12px',
          minHeight: '100%',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          p: 3
        }}>
          {renderView()}
        </Box>
      </Box>
    </Box>
  );
}

export default DashBoard;